/* eslint-disable react-refresh/only-export-components -- utility module, not a hot-reloaded component file */
import { type ReactNode } from 'react'
import {
  classifyDecision,
  describeHold,
  reasoningFor,
  showsReasoning,
  summarizeReceipt,
  toReceipt,
  type DecisionClass,
  type Receipt,
} from '@marva/shared/decision-receipt'
import { type Activity, type CampaignDecisionRow, type ShadowDecision } from '../services/api'

// Shared activity-feed presentation: turning the structured activity record into
// prose in Marva's voice, folding issued discount codes into the send they rode
// on, and humanising the chips. Shared by the Journal, Ledger's drawer and the
// Inbox drawer (it was the Journal and the Today preview, until Today retired —
// MAR-309) so they never drift.

export function fmtCurrency(n: number) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0)
}

// A derived activity: the discount code that rode on a send is folded into its
// parent so one decision reads as one line (mergedCode / mergedPercent).
export type DisplayActivity = Activity & { mergedCode?: string; mergedPercent?: number }

/**
 * What happened to a decision entry — the shared classifier's receipt (MAR-64).
 *
 * The activity feed computes it with `@marva/shared/decision-receipt`; an older feed that
 * sent none gets the SAME function run here over the same fields. Nothing in this app maps
 * `action`/`status` to a class by hand: the legacy mirror calls a model failure `skip` and a
 * cooldown-blocked help `engage`, and a hand-rolled mapping gets exactly those rows wrong.
 *
 * `null` for entries that are not decisions (conversations, discounts, campaigns, emails) —
 * they have no receipt, and inventing one would be the same lie the other way round. A shadow
 * entry is classified as `shadowed`, which is what the feed states it as.
 */
export function receiptOf(a: DisplayActivity): Receipt | null {
  if (!isDecisionEntry(a)) return null
  if (a.receipt) return a.receipt
  // A shadow entry is stated as what the feed shows it as: shadow mode, `shadowed`.
  const shadow = a.source === 'shadow' || a.type === 'shadow_decision'
  return toReceipt(classifyDecision(shadow ? { ...a, mode: 'shadow', status: 'shadowed' } : a))
}

/**
 * The reasoning a merchant may read for an entry — "Why I did this". A decision failure writes
 * its ERROR as the row's reasoning; a current feed already nulls it, but an older one sends it
 * raw, so the shared `showsReasoning` rule is applied here too. Non-decision entries (a
 * conversation's summary doubles as its reasoning) are untouched.
 */
export function visibleReasoning(a: DisplayActivity): string | undefined {
  const r = receiptOf(a)
  if (r && !showsReasoning(r)) return undefined
  return a.reasoning || undefined
}

/**
 * A held shadow decision (the "Needs you" queue, the inbox drawer) as the queue states it:
 * shadow mode, `shadowed` — the same statement the shadow reads and the activity feed make.
 */
function asShadowRow(d: ShadowDecision) {
  return { ...d, mode: 'shadow', status: 'shadowed' }
}

/**
 * The reasoning a merchant may read on a held shadow decision. A current workspaces Lambda
 * already nulls a failure's error text, but an older one (deploy skew) sends the raw row, so
 * the SAME shared `reasoningFor` rule runs here — never `d.reasoning` directly.
 */
export function shadowReasoning(d: ShadowDecision): string | undefined {
  const row = asShadowRow(d)
  return reasoningFor(row, classifyDecision(row)) || undefined
}

/**
 * What a held shadow decision says when there is no reasoning to show — the shared receipt
 * line, so a model failure reads "Couldn't make a call … a fault on our side" rather than
 * "Marva proposed to engage".
 */
export function shadowSummary(d: ShadowDecision): string {
  return summarizeReceipt(asShadowRow(d))
}

// Marva choosing silence (`wait`), and a pre-D2 skip nobody can say more about
// (`legacy_ambiguous`). Nothing ever revisits these records — a later re-trigger logs a NEW
// decision — so neither is "pending". They are low-signal on their own and arrive in bursts (a
// self-browsing storm can log a dozen in minutes), so the Journal collapses consecutive runs of
// them into one line (as the Today preview did, until MAR-309); the test is shared so any feed
// that collapses them collapses the same ones.
//
// Deliberately NOT here: a failure (not a choice), a governance block (your rule, not her
// restraint), an escalation (it needs you). Folding those into a "held off" run is how a
// Bedrock outage used to read as tact.
export function isHeldOff(a: DisplayActivity): boolean {
  const r = receiptOf(a)
  return !!r && (r.class === 'wait' || r.class === 'legacy_ambiguous')
}

// Did this entry do something for a shopper — sent, on its way, or scheduled to go? Keyed by
// every class, so a new class is a compile error until someone decides whether it counts.
const HANDLED_CLASS: Record<DecisionClass, boolean> = {
  help_executed: true,
  help_in_flight: true,
  help_deferred: true,
  decision_failed: false,
  legacy_ambiguous: false,
  wait: false,
  escalate: false,
  execution_failed: false,
  help_blocked: false,
  help_withdrawn: false,
  help_shadowed: false,
}

/**
 * Counts toward "handled" in the Journal day summary (and did on Today, until MAR-309). A decision counts only when
 * its receipt says a help went (or is going) out; a non-decision entry (a conversation, an
 * issued code, a campaign roll-up) counts as it always did, and a shadow entry never does.
 */
export function isHandled(a: DisplayActivity): boolean {
  const r = receiptOf(a)
  if (!r) return a.type !== 'shadow_decision'
  return HANDLED_CLASS[r.class] && !r.unrecognisedStatus
}

/** The collapsed line for a run of `isHeldOff` entries — honest about what the run holds. */
export function heldRunLabel(items: DisplayActivity[]): { line: string; chip: string } {
  const allWaits = items.length > 0 && items.every((a) => receiptOf(a)?.class === 'wait')
  return allWaits
    ? { line: `Chose to wait ${items.length} times — nothing needed you.`, chip: `Chose to wait · ${items.length}` }
    : { line: `Didn’t reach out ${items.length} times.`, chip: `Not sent · ${items.length}` }
}

/**
 * Is this feed row backed by a real `decisionId`?
 *
 * ⚠️ `Activity.id` is NOT a decision id for most rows, and the field gives no clue:
 * `activity-feed` sets it from `decision.decisionId` for exactly two shapes
 * (`index.js:352` live, `:1966` shadow) and from something else for every other —
 * `groupKey` for aggregated campaigns, `conversationId` for conversations,
 * `emailId || sk` for emails, `codeId || sk` for discounts. All of them are plain
 * strings, so nothing downstream can tell them apart by looking.
 *
 * `source` can, and is set by the same formatters in the same place, on the list
 * AND on the detail response (`getActivityDetail` re-formats through
 * `formatDecisionActivity` / `formatShadowDecisionActivity`), so a row does not
 * change its answer when the drawer re-titles itself from the detail fetch.
 *
 * This exists because of what pinning a non-decision as `type: 'decision'` COSTS
 * once the pin reaches the server (MAR-174 §3.2). The id shape is permissive
 * enough that a `groupKey` or a `conv_…` id does not 400 — it is accepted,
 * fails its ownership check, and narrows. Harmless per turn, and corrosive in
 * aggregate: `AskContextNarrowed{reason:entity_not_proven, entityType:decision}`
 * could not then separate "a merchant pasted a foreign id" from "the Journal
 * pinned a conversation as a decision", and the metric would be noise from the
 * day it shipped.
 */
export function isDecisionEntry(a: DisplayActivity): boolean {
  return a.source === 'decision' || a.source === 'shadow'
}

const TYPE_LABEL: Record<string, string> = {
  cart_recovery: 'Cart recovery',
  browse_followup: 'Browse follow-up',
  comparison: 'Comparison',
  inspiration: 'Product inspiration',
  conversation: 'Conversation',
  back_in_stock: 'Back in stock',
  price_drop: 'Price drop',
  shadow_decision: 'Held decision',
  discount: 'Discount',
  agent_decision: 'Agent decision',
  proactive_greeting: 'Proactive greeting',
  exit_intervention: 'Exit help',
  contact_capture: 'Contact captured',
  product_feedback: 'Product feedback',
}
export function typeLabel(type?: string) {
  return (type && TYPE_LABEL[type]) || (type || 'Journal entry').replace(/_/g, ' ')
}

// Human nouns for the person on the other side — never raw segment codes.
const SEGMENT_LABEL: Record<string, string> = {
  high_value: 'VIP', vip: 'VIP', high: 'High value',
  at_risk: 'At risk', loyal: 'Loyal', returning: 'Returning',
  repeat_customer: 'Returning', new: 'New', new_visitor: 'New',
}
const OUTCOME_LABEL: Record<string, string> = {
  email_sent: 'Sent', sms_sent: 'Sent', sent: 'Sent', chat_message_sent: 'Sent',
  delivered: 'Delivered', email_delivered: 'Delivered', sms_delivered: 'Delivered',
  email_opened: 'Opened', opened: 'Opened',
  email_clicked: 'Clicked', sms_clicked: 'Clicked', clicked: 'Clicked',
  converted: 'Converted', purchased: 'Converted', conversion: 'Converted',
  popup_displayed: 'Shown',
  dismissed: 'Dismissed', no_engagement: 'No response', no_open: 'Not opened',
}
export function segmentLabel(a: Activity) {
  const raw = (a.customerSegment || a.customerSnapshot?.valueSegment || '').toLowerCase()
  return SEGMENT_LABEL[raw]
}
export function outcomeLabel(o?: string) {
  if (!o) return undefined
  return OUTCOME_LABEL[o] || o.replace(/_/g, ' ')
}
// Revenue attributable to an entry. Individual decisions carry `revenue`;
// aggregated campaign rows carry the summed `metadata.totalRevenue` instead —
// day summaries and chips must read both or a campaign conversion counts as £0.
export function activityRevenue(a: DisplayActivity): number | undefined {
  if (a.revenue != null) return a.revenue
  const agg = a.metadata?.isAggregated ? a.metadata.totalRevenue : undefined
  return agg != null && agg > 0 ? agg : undefined
}
// A short, human description of who Marva was dealing with.
export function whoLabel(a: Activity) {
  const seg = (a.customerSegment || a.customerSnapshot?.valueSegment || '').toLowerCase()
  const life = (a.lifecycleStage || a.customerSnapshot?.lifecycleStage || '').toLowerCase()
  if (seg === 'high_value' || seg === 'vip') return 'a VIP customer'
  if (seg === 'at_risk') return 'an at-risk customer'
  if (life === 'new' || life === 'new_visitor') return 'a new shopper'
  if (life === 'repeat_customer' || life === 'returning' || life === 'loyal') return 'a returning customer'
  return a.metadata?.customerId ? 'a customer' : 'a shopper'
}
export function codeFromDesc(desc?: string) {
  return /code:\s*([a-z0-9-]+)/i.exec(desc || '')?.[1]
}
export function pctFromDesc(desc?: string) {
  const m = /(\d+)%/.exec(desc || '')
  return m ? Number(m[1]) : undefined
}

function Em({ children }: { children: ReactNode }) {
  return <span className="mv-em">{children}</span>
}

// The help she proposed, as an infinitive ("remind a shopper about their abandoned cart"), so
// every class can frame the same act: chose to wait rather than…, wanted to…, but…, planned
// to…, tried to…. One phrasing per type, so the class is the only thing that changes.
function helpPhrase(a: DisplayActivity, w: string): ReactNode {
  const product = a.productTitle
  const cart = a.cartValue ? fmtCurrency(a.cartValue) : null
  switch (a.type) {
    case 'cart_recovery':
      return <>remind {w} about {cart ? <>the <Em>{cart}</Em> they left behind</> : <>their abandoned cart</>}</>
    case 'browse_followup':
      return <>follow up with {w} after they browsed{product ? <> the <Em>{product}</Em></> : null}</>
    case 'back_in_stock':
      return <>let {w} know{product ? <> the <Em>{product}</Em></> : <> an item they wanted</>} is back in stock</>
    case 'price_drop':
      return <>tell {w}{product ? <> the <Em>{product}</Em></> : <> something they liked</>} dropped in price</>
    case 'inspiration':
      return <>send {w} a few hand-picked ideas</>
    case 'proactive_greeting':
      return <>greet {w} while they were browsing</>
    case 'exit_intervention':
      return <>offer {w} a hand on their way out</>
    case 'comparison':
      return <>help {w} weigh up the options they were comparing</>
    case 'product_feedback':
      return <>ask {w} what they thought{product ? <> of the <Em>{product}</Em></> : null}</>
    case 'contact_capture':
      return <>invite {w} to leave their details</>
  }
  switch (a.trigger) {
    case 'confusion_signal':
      return <>step in while {w} seemed stuck</>
    case 'comparison_loop':
      return <>help {w} choose between the options they kept comparing</>
    case 'product_dwell':
      return <>offer {w} a hand{product ? <> with the <Em>{product}</Em></> : null}</>
  }
  return <>reach out to {w}</>
}

// A send, in the past tense — what she did. The only phrasing for `help_executed` and a
// recognised `help_in_flight`, and for entries that are not decisions at all.
function sentNarrative(a: DisplayActivity, w: string): ReactNode {
  const product = a.productTitle
  const cart = a.cartValue ? fmtCurrency(a.cartValue) : null
  const pct = a.mergedPercent ?? a.discount?.percent ?? a.metadata?.discountPercent ?? pctFromDesc(a.description)
  const dcode = a.mergedCode || a.metadata?.code || codeFromDesc(a.description)
  switch (a.type) {
    case 'cart_recovery':
      return (
        <>
          Reminded {w} about {cart ? <>the <Em>{cart}</Em> they left behind</> : <>their abandoned cart</>}
          {pct ? <> — with a <Em>{pct}% code</Em> to close it</> : null}.
        </>
      )
    case 'browse_followup':
      return (
        <>
          Followed up with {w} after they browsed{product ? <> the <Em>{product}</Em></> : null}
          {pct ? <>, adding a <Em>{pct}% nudge</Em></> : null}.
        </>
      )
    case 'back_in_stock':
      return <>Let {w} know{product ? <> the <Em>{product}</Em></> : <> an item they wanted</>} is back in stock.</>
    case 'price_drop':
      return <>Told {w}{product ? <> the <Em>{product}</Em></> : <> something they liked</>} just dropped in price.</>
    case 'inspiration':
      return <>Sent {w} a few hand-picked ideas to come back to.</>
    case 'proactive_greeting':
      return <>Stepped in to greet {w} while they were browsing.</>
    case 'exit_intervention':
      return <>Caught {w} on their way out and offered a hand.</>
    case 'comparison':
      return <>Helped {w} weigh up the options they were comparing.</>
    case 'product_feedback':
      return <>Asked {w} what they thought{product ? <> of the <Em>{product}</Em></> : null}.</>
    case 'contact_capture':
      return <>Invited {w} to leave their details so she could follow up.</>
    case 'conversation':
      return <>Chatted with {w}.</>
    case 'discount':
      return (
        <>
          Issued {pct ? <>a <Em>{pct}% code</Em></> : <>a discount code</>}
          {dcode ? <> (<Em>{dcode}</Em>)</> : null} for {w}.
        </>
      )
    case 'shadow_decision':
      return shadowNarrative(a, w)
    case 'agent_decision':
    default:
      // An agent_decision is a real LLM call with no dedicated type — say what
      // it actually was, from the trigger, rather than falling back to a label.
      switch (a.trigger) {
        case 'confusion_signal':
          return <>Noticed {w} seemed stuck and stepped in to help.</>
        case 'comparison_loop':
          return <>Helped {w}, who kept going back and forth between options.</>
        case 'high_intent_anonymous':
          return <>Spotted strong buying signals from {w} and reached out.</>
        case 'product_dwell':
          return <>Saw {w} lingering{product ? <> on the <Em>{product}</Em></> : <> on a product</>} and offered a hand.</>
      }
      if (a.signalType || a.intentType || a.pageType)
        return <>Made a judgement call{a.pageType ? <> on the <Em>{a.pageType}</Em> page</> : null} for {w}.</>
      if (a.type === 'agent_decision') return <>Made a judgement call about {w}.</>
      return a.title && a.title !== a.reasoning ? a.title : typeLabel(a.type)
  }
}

// Shadow mode: reasoned, never eligible to send. A shadow draft that failed (`draftFailed`) is
// still this — it was never going to be sent, so it is never "tried".
function shadowNarrative(a: DisplayActivity, w: string): ReactNode {
  // 'Agent decision' is the feed's placeholder when it has no reasoning to show.
  return a.description && a.description !== a.reasoning && a.description !== 'Agent decision'
    ? a.description
    : <>Decided what she’d do for {w} — and held it for your review.</>
}

// "Mon 21 Sep, 08:00" — the merchant's local clock, for a deferral's `until`.
function fmtUntil(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return d.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

type NarrativeCtx = { a: DisplayActivity; r: Receipt; w: string; help: ReactNode }

// The feed one-liner for a decision, one entry per receipt class. A `Record` over the whole
// union: a class added to the classifier is a compile error here until it has words.
//
// Two rules the wording keeps: a failure is never phrased as restraint (no "held", no
// "chose", no "wait" — it was not a choice), and restraint is only ever the agent's own
// `wait`. A governance hold names the rule through `describeHold`, the one phrase table.
const CLASS_NARRATIVE: Record<DecisionClass, (c: NarrativeCtx) => ReactNode> = {
  decision_failed: ({ w }) => <>Couldn’t make a call about {w} — a fault on our side, not a decision.</>,
  legacy_ambiguous: ({ w }) => <>Didn’t reach out to {w} — recorded before reasons were kept apart.</>,
  wait: ({ help }) => <>Chose to wait rather than {help}.</>,
  escalate: ({ help }) => <>Wanted your input on whether to {help} — nothing was sent.</>,
  execution_failed: ({ help }) => <>Tried to {help}, but it didn’t go through.</>,
  help_blocked: ({ r, help }) =>
    r.pendingApproval
      ? <>Wants to {help} — waiting on your approval.</>
      : <>Wanted to {help}, but {describeHold({ reasonCode: r.reasonCode, source: r.source })}.</>,
  help_withdrawn: ({ r, help }) =>
    <>Planned to {help}, but it {r.reasonCode === 'expired' ? 'expired' : 'was withdrawn'} before sending.</>,
  help_deferred: ({ r, help }) => {
    const until = fmtUntil(r.until)
    return until ? <>Planned to {help}; deferred until <Em>{until}</Em>.</> : <>Planned to {help}; deferred for later.</>
  },
  help_shadowed: ({ a, w }) => shadowNarrative(a, w),
  help_executed: ({ a, w }) => sentNarrative(a, w),
  help_in_flight: ({ a, r, w, help }) =>
    r.unrecognisedStatus ? <>Planned to {help} — outcome not recorded.</> : sentNarrative(a, w),
}

// The feed one-liner — what she *did*, in her own voice. The "why" (reasoning)
// stays in the drawer. Conversations keep their real agent-written summary.
// Classes whose line also says the help had been deferred first, when it had. The provenance
// the D2 split exists to keep: "sent" and "sent after being deferred until morning" are
// different receipts. `help_deferred` names its own `until`; the rest proposed nothing.
const SAYS_PRIOR_DEFERRAL: Record<DecisionClass, boolean> = {
  help_executed: true,
  help_in_flight: true,
  help_blocked: true,
  execution_failed: true,
  help_withdrawn: true,
  help_deferred: false,
  help_shadowed: false,
  decision_failed: false,
  legacy_ambiguous: false,
  wait: false,
  escalate: false,
}

export function narrative(a: DisplayActivity): ReactNode {
  // Aggregated campaigns speak in totals — the per-person phrasings below
  // would misread a multi-recipient send as "a shopper".
  if (a.metadata?.isAggregated) return a.description || typeLabel(a.type)
  if (a.content?.summary) return a.content.summary
  const w = whoLabel(a)
  const r = receiptOf(a)
  if (!r) return sentNarrative(a, w)
  const line = CLASS_NARRATIVE[r.class]({ a, r, w, help: helpPhrase(a, w) })
  const deferred = SAYS_PRIOR_DEFERRAL[r.class] ? fmtUntil(r.deferredUntil) : null
  // Worded "deferred", never "held"/"waited": on a failure line those words would read as
  // restraint.
  return deferred ? <>{line} It had been deferred until <Em>{deferred}</Em>.</> : line
}

export type ActionPill = { label: string; tone: 'gold' | 'sage' | 'neutral' }

// A send with nothing contrary on record: the outcome if there is one, else what the delivery
// evidence says, else the plain send word for the channel.
function sentPill(a: DisplayActivity): ActionPill {
  const out = outcomeLabel(a.outcome)
  if (out) return { label: out, tone: a.outcome === 'converted' || a.outcome === 'conversion' ? 'gold' : 'sage' }
  // Socket channels stamp what the visitor actually experienced — a message
  // queued for their next visit must not read as "Reached out".
  if (a.deliveryStatus === 'queued') return { label: 'Not seen yet', tone: 'neutral' }
  // Positive delivery evidence, graded. Only these two are worth saying out loud:
  // 'accepted' is an API Gateway hand-off and 'none' is silence, and neither earns a
  // stronger word than the plain send label.
  if (a.deliveryProof === 'confirmed') return { label: 'Delivered', tone: 'neutral' }
  if (a.deliveryProof === 'rendered') return { label: 'Seen', tone: 'neutral' }
  return { label: a.channel === 'email' || a.channel === 'sms' ? 'Sent' : 'Reached out', tone: 'neutral' }
}

// What a merchant reads for each receipt class. Keyed by the WHOLE class union: a class added
// to the classifier without deciding what it says here is a compile error. (The status-keyed
// table this replaced let a failure written `skip/dispatched` read "Held off", and a
// cooldown-blocked help reach "Sent" whenever its status was not the one it expected.)
//
// An outcome only speaks for a class where something may have gone out; where nothing did, an
// outcome on the row must not resurrect it into a delivery label.
const CLASS_PILL: Record<DecisionClass, (a: DisplayActivity, r: Receipt) => ActionPill> = {
  // Not a choice and not restraint — the system broke before a judgment existed.
  decision_failed: () => ({ label: 'Fault on our side', tone: 'gold' }),
  // A pre-D2 skip: no record of why. Say only what is known.
  legacy_ambiguous: () => ({ label: 'Not sent', tone: 'neutral' }),
  wait: () => ({ label: 'Chose to wait', tone: 'sage' }),
  escalate: () => ({ label: 'Needs your input', tone: 'sage' }),
  // A bounce outranks both status and outcome: a bounced email keeps `status: executed` and
  // `outcome: email_sent`, so reading either would tell a merchant we reached someone we
  // demonstrably did not.
  execution_failed: (_a, r) =>
    r.failurePhase === 'delivery'
      ? { label: 'Not delivered', tone: 'gold' }
      : { label: 'Didn’t go through', tone: 'gold' },
  // A governance hold is the system working, not a fault — it reads like the other held
  // states. Waiting on the merchant's approval is its own thing.
  help_blocked: (_a, r) =>
    r.pendingApproval ? { label: 'Waiting on you', tone: 'sage' } : { label: 'Held back', tone: 'sage' },
  help_withdrawn: (_a, r) =>
    r.reasonCode === 'expired' ? { label: 'Expired', tone: 'neutral' } : { label: 'Cancelled', tone: 'neutral' },
  // In flight: an outcome beats it, because on the email path nothing flips the status when
  // the send executes but an outcome existing proves it did.
  help_deferred: (a) => (outcomeLabel(a.outcome) ? sentPill(a) : { label: 'Scheduled', tone: 'neutral' }),
  help_shadowed: () => ({ label: 'Held for review', tone: 'sage' }),
  help_executed: (a) => sentPill(a),
  help_in_flight: (a, r) => {
    // A status this build has never heard of (an older tab against a newer backend), or a
    // help carrying a wait's terminal status — say what we know, not a send we cannot vouch for.
    if (r.unrecognisedStatus) return { label: 'Unconfirmed', tone: 'neutral' }
    if (outcomeLabel(a.outcome)) return sentPill(a)
    // Handed off with nothing back yet — NOT "Sent".
    if (a.status === 'dispatched') return { label: 'Sending', tone: 'neutral' }
    // A socket message parked for the visitor's next visit.
    if (a.status === 'pending_delivery') return { label: 'Not seen yet', tone: 'neutral' }
    return sentPill(a)
  },
}

// The single status pill for an entry: what became of it. Never an internal status string
// ("executed", "logged") or the bare channel. Decisions answer from their receipt; anything
// else (a conversation, an issued code, a campaign roll-up) has only its outcome to say.
export function actionState(a: DisplayActivity): ActionPill | undefined {
  const r = receiptOf(a)
  if (r) return CLASS_PILL[r.class](a, r)
  const out = outcomeLabel(a.outcome)
  return out ? { label: out, tone: a.outcome === 'converted' || a.outcome === 'conversion' ? 'gold' : 'sage' } : undefined
}

// The caption over the drawer's message box. Tenses, because there are several truths: it
// never went, it has not gone yet, it went, or we tried and it broke.
const CLASS_CONTENT_LABEL: Record<DecisionClass, (a: DisplayActivity, r: Receipt) => string> = {
  decision_failed: () => 'What I would have sent',
  legacy_ambiguous: () => 'What I would have sent',
  wait: () => 'What I would have sent',
  escalate: () => 'What I would have sent',
  // A bounce: we did send it, it just did not land. Otherwise it broke on our side first.
  execution_failed: (_a, r) => (r.failurePhase === 'delivery' ? 'What I sent' : 'What I tried to send'),
  help_blocked: () => 'What I would have sent',
  help_withdrawn: () => 'What I would have sent',
  help_deferred: () => "What I'll send",
  help_shadowed: () => 'What I would have sent',
  help_executed: () => 'What I sent',
  help_in_flight: (a, r) => {
    if (r.unrecognisedStatus) return 'What I planned to send'
    if (a.status === 'dispatched' || a.status === 'pending_delivery') return "What I'm sending"
    return 'What I sent'
  },
}

export function contentLabel(a: DisplayActivity): string {
  const r = receiptOf(a)
  return r ? CLASS_CONTENT_LABEL[r.class](a, r) : 'What I sent'
}

// Prefix for the drawer's send-window line ("Scheduled for …" / "Was scheduled for …"). Only a
// proposed help ever had a send window; a wait, an escalation, a failure before any judgment,
// a legacy skip and a shadow draft had none, so they get no line.
const CLASS_HAD_WINDOW: Record<DecisionClass, boolean> = {
  decision_failed: false,
  legacy_ambiguous: false,
  wait: false,
  escalate: false,
  help_shadowed: false,
  execution_failed: true,
  help_blocked: true,
  help_withdrawn: true,
  help_deferred: true,
  help_executed: true,
  help_in_flight: true,
}

export function scheduleLinePrefix(a: DisplayActivity): string | null {
  const r = receiptOf(a)
  if (r && !CLASS_HAD_WINDOW[r.class]) return null
  return r?.class === 'help_deferred' ? 'Scheduled for' : 'Was scheduled for'
}

/**
 * The receipt of one per-person row behind an aggregated campaign. The feed sends it; an
 * older feed did not, and then the row's own legacy side (`engaged` / `skipped`) is all there
 * is to classify — run through the same classifier, so a bare `skip` reads as
 * `legacy_ambiguous` rather than as a wait.
 */
export function campaignRowReceipt(d: CampaignDecisionRow, side: 'engaged' | 'skipped'): Receipt {
  if (d.receipt) return d.receipt
  return toReceipt(classifyDecision({
    action: side === 'engaged' ? 'engage' : 'skip',
    status: d.status,
    outcome: d.outcome,
  }))
}

/** The one-line pill for a campaign's per-person row — the same table as a full entry. */
export function campaignRowPill(d: CampaignDecisionRow, side: 'engaged' | 'skipped'): ActionPill {
  const r = campaignRowReceipt(d, side)
  const row = { id: d.id, source: 'decision', status: d.status, outcome: d.outcome, receipt: r } as DisplayActivity
  return CLASS_PILL[r.class](row, r)
}

/** The reasoning a campaign row may show — the same shared rule as a full entry. */
export function campaignRowReasoning(d: CampaignDecisionRow, side: 'engaged' | 'skipped'): string | undefined {
  return showsReasoning(campaignRowReceipt(d, side)) ? d.reasoning || undefined : undefined
}

/** The hold on a blocked campaign row, in the merchant's words. */
export function campaignRowHold(d: CampaignDecisionRow): string | null {
  const r = campaignRowReceipt(d, 'engaged')
  if (r.class !== 'help_blocked') return null
  return r.pendingApproval ? 'waiting on your approval' : describeHold({ reasonCode: r.reasonCode, source: r.source })
}

// Fold each issued discount code into the send it belongs to (same customer,
// matching channel, within 15 min) so one event is one line. Codes with no
// parent stay as their own entry. Returns shallow clones — never mutates input.
const DISCOUNT_CHANNEL_TYPES: Record<string, string[]> = {
  cart_recovery: ['cart_recovery'],
  post_browse: ['browse_followup'],
  inspiration: ['inspiration'],
  comparison: ['comparison'],
  chat: ['conversation', 'proactive_greeting', 'exit_intervention', 'agent_decision'],
  email_reply: ['conversation'],
  sms: ['cart_recovery', 'back_in_stock', 'price_drop'],
}
export function mergeDiscounts(list: Activity[]): DisplayActivity[] {
  const rest: DisplayActivity[] = list.filter((a) => a.type !== 'discount').map((a) => ({ ...a }))
  const discounts = list.filter((a) => a.type === 'discount')
  for (const d of discounts) {
    const dCust = d.metadata?.customerId || d.metadata?.visitorId
    const dTime = new Date(d.timestamp).getTime()
    const wantTypes = d.metadata?.channel ? DISCOUNT_CHANNEL_TYPES[d.metadata.channel] : undefined
    let best: DisplayActivity | undefined
    let bestScore = Infinity
    for (const p of rest) {
      const pCust = p.metadata?.customerId || p.metadata?.visitorId
      if (!dCust || !pCust || pCust !== dCust) continue
      if (wantTypes && !wantTypes.includes(p.type)) continue
      const delta = Math.abs(new Date(p.timestamp).getTime() - dTime)
      if (delta > 15 * 60 * 1000) continue
      // Prefer a parent that already recorded offering a discount.
      const score = delta - (p.discount ? 5 * 60 * 1000 : 0)
      if (score < bestScore) {
        bestScore = score
        best = p
      }
    }
    if (best) {
      best.mergedCode = d.metadata?.code || codeFromDesc(d.description)
      best.mergedPercent = d.metadata?.discountPercent ?? best.discount?.percent ?? pctFromDesc(d.description)
    } else {
      rest.push({ ...d })
    }
  }
  return rest.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
}
